import { Receipt } from '@gravity-ui/icons'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { useTimesheetStore } from '@/store/timesheet.store'

import { TimesheetExpenseRow } from './TimesheetExpenseRow'

export function DayDetailsCard() {
  const { t, i18n } = useTranslation()
  const { selectedDay, filters, expenses: globalExpenses, isLoading: isGlobalLoading } = useTimesheetStore()

  // Filter expenses for the specific day, or show all if no day selected
  const expenses = selectedDay
    ? globalExpenses.filter(e => {
      const targetDateStr = `${selectedDay.date.getFullYear()}-${String(selectedDay.date.getMonth() + 1).padStart(2, '0')}-${String(selectedDay.date.getDate()).padStart(2, '0')}`
      return e.date?.startsWith(targetDateStr)
    })
    : globalExpenses

  const groupedExpenses = expenses.reduce((acc, exp) => {
    const dateStr = exp.date?.split('T')[0] || 'Unknown'
    if (!acc[dateStr]) acc[dateStr] = []
    acc[dateStr].push(exp)
    return acc
  }, {} as Record<string, typeof expenses>)

  // Removed early return for no selectedDay

  return (
    <Card className="h-full flex flex-col" variant='transparent'>
      <CardHeader className="flex-col items-start gap-1">
        <div className="text-lg font-bold">{t('timesheet.expenses')}</div>
        <div className="text-sm text-foreground/60">
          {selectedDay
            ? selectedDay.date.toLocaleDateString(i18n.resolvedLanguage === 'ru' ? 'ru-RU' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
            : `${String(filters.month).padStart(2, '0')}.${filters.year}`
          }
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-y-auto px-2 pb-6 pt-0 relative">
        <AnimatePresence mode="wait">
          {!filters.driverID ? (
            <motion.div
              key="empty-driver"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.1 }}
              className="h-full flex items-center justify-center"
            >
              <EmptyState
                icon={<Receipt className="size-8" />}
                title={t('timesheet.select_driver_prompt')}
                description={''}
              />
            </motion.div>
          ) : isGlobalLoading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center p-8"
            >
              <span className="text-sm text-foreground/50 animate-pulse">{t('common.loading')}</span>
            </motion.div>
          ) : expenses.length === 0 ? (
            <motion.div
              key="empty-none"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.1 }}
              className="h-full flex items-center justify-center"
            >
              <EmptyState
                icon={<Receipt className="size-8" />}
                title={t('analytics.no_expenses', { defaultValue: 'No expenses recorded for this day.' })}
                description={''}
              />
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              <div>
                <div className="space-y-2">
                  <AnimatePresence mode="popLayout">
                    {Object.entries(groupedExpenses)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([dateStr, exps], index, array) => (
                        <motion.div
                          key={dateStr}
                          layout
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          className="space-y-1"
                        >
                          <div className="text-sm font-semibold text-foreground/50 sticky top-0 bg-content1/80 backdrop-blur z-10 py-1">
                            {dateStr === 'Unknown'
                              ? t('common.unknown_date')
                              : new Date(dateStr).toLocaleDateString(i18n.resolvedLanguage === 'ru' ? 'ru-RU' : 'en-US', { day: 'numeric', month: 'long', weekday: 'short' })
                            }
                          </div>

                          <div className="space-y-1">
                            {exps.map((exp, i) => (
                              <TimesheetExpenseRow key={`${exp.name}-${i}`} expense={exp} />
                            ))}
                          </div>

                          {index < array.length - 1 && <div className="h-px w-full bg-divider mt-3" />}
                        </motion.div>
                      ))}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  )
}
